import { useRef, useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import ProductCard from '../products/ProductCard';

interface ProductCardData {
  id: string;
  name: string;
  imageUrl: string;
  price: number;
  originalPrice?: number;
  discountPercentage?: number;
  isFeatured?: boolean;
  isBestSeller?: boolean;
  isOutOfStock?: boolean;
  defaultVariantId?: string;
  hasMultipleVariants?: boolean;
  hasOptionGroups?: boolean;
}

interface ProductSliderProps {
  title: string;
  products: ProductCardData[];
  viewAllLink?: string;
  /** Maximum number of products to display in the slider. */
  maxItems?: number;
}

// Default cap on how many products a homepage slider shows. The full set is
// reachable via the "View All" link.
const DEFAULT_MAX_ITEMS = 10;

export default function ProductSlider({
  title,
  products,
  viewAllLink,
  maxItems = DEFAULT_MAX_ITEMS,
}: ProductSliderProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  // Minimum swipe distance (in px)
  const minSwipeDistance = 50;

  const checkScrollability = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    checkScrollability();
    window.addEventListener('resize', checkScrollability);
    return () => window.removeEventListener('resize', checkScrollability);
  }, [products]);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = scrollContainerRef.current.clientWidth * 0.8;
      const newScrollLeft =
        direction === 'left'
          ? scrollContainerRef.current.scrollLeft - scrollAmount
          : scrollContainerRef.current.scrollLeft + scrollAmount;

      scrollContainerRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth',
      });
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && canScrollRight) {
      scroll('right');
    } else if (isRightSwipe && canScrollLeft) {
      scroll('left');
    }
  };

  // Don't render if no products
  if (products.length === 0) {
    return null;
  }

  const visibleProducts = products.slice(0, maxItems);

  return (
    <section className="storefront-section-spacing">
      <div className="storefront-container">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl md:text-3xl font-semibold text-neutral-900">
            {title}
          </h2>
          {viewAllLink && (
            <Link
              to={viewAllLink}
              className="inline-flex items-center gap-1 text-sm font-medium text-brand-primary hover:text-brand-primary-hover transition-colors duration-fast"
            >
              View All
              <ArrowRight className="h-4 w-4" />
            </Link>
          )}
        </div>

        {/* Slider Container */}
        <div className="relative">
          {/* Left Arrow */}
          {canScrollLeft && (
            <button
              onClick={() => scroll('left')}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full bg-white shadow-lg hover:bg-neutral-50 hover:shadow-xl transition-all duration-300 -translate-x-1/2"
              aria-label="Scroll left"
            >
              <ChevronLeft className="h-6 w-6 text-neutral-900" />
            </button>
          )}

          {/* Right Arrow */}
          {canScrollRight && (
            <button
              onClick={() => scroll('right')}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full bg-white shadow-lg hover:bg-neutral-50 hover:shadow-xl transition-all duration-300 translate-x-1/2"
              aria-label="Scroll right"
            >
              <ChevronRight className="h-6 w-6 text-neutral-900" />
            </button>
          )}

          {/* Products Container */}
          <div
            ref={scrollContainerRef}
            onScroll={checkScrollability}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            className="flex gap-4 overflow-x-auto overflow-y-visible scrollbar-hide scroll-smooth py-4 -my-4 px-2 -mx-2"
            style={{
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
            }}
          >
            {visibleProducts.map((product) => (
              <div
                key={product.id}
                className="flex-none w-[calc(50%-0.5rem)] sm:w-[calc(33.333%-0.667rem)] lg:w-[calc(25%-0.75rem)] xl:w-[calc(20%-0.8rem)]"
              >
                <ProductCard {...product} />
              </div>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .slider-container .opacity-0 {
          transition: opacity 300ms ease-in-out, transform 300ms ease-in-out;
        }
        .slider-container:hover .opacity-0 {
          opacity: 1;
        }
      `}</style>
    </section>
  );
}
